import React, { useState } from 'react';

import classNames from 'classnames';

import { RECORD_TYPES, statusBadge, typeTagClass } from './approvalsHelpers';
import { tableMoney } from './planHelpers';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import EyeIcon from 'Assets/icons/eye.svg';

const COLUMNS = [
    { key: 'reference', label: $t('Reference') },
    { key: 'type', label: $t('Type') },
    { key: 'procurement', label: $t('Procurement') },
    { key: 'department', label: $t('Department') },
    { key: 'value', label: $t('Value'), align: 'end' },
    { key: 'requestor', label: $t('Requestor') },
    { key: 'raised', label: $t('Raised') },
    { key: 'status', label: $t('Status') },
    { key: 'actions', label: $t('Actions') },
];

const StatusPill = ({ status }) => {
    const badge = statusBadge(status);

    return (
        <span className={classNames('tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold', badge.pill)}>
            {badge.showDot && <span className={classNames('tw-h-2 tw-w-2 tw-rounded-full tw-shrink-0', badge.dot)} />}
            {badge.label}
        </span>
    );
};

const TypeTag = ({ type }) => (
    <span className={classNames('tw-inline-flex tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold', typeTagClass(type))}>{type}</span>
);

const ViewButton = ({ row, onView }) => (
    <button
        type="button"
        onClick={() => onView(row)}
        className="tw-inline-flex tw-text-charcoal-50 hover:tw-text-charcoal"
        aria-label={$t('View')}
        title={$t('View')}
    >
        <EyeIcon className="tw-w-3.5 tw-h-3.5" />
    </button>
);

/**
 * The linked PR revealed beneath an EA row. Styled as an indented, tinted continuation of the
 * register — same columns as a top-level row, minus the disclosure control.
 */
const LinkedPrRow = ({ pr, currencySymbol, onView }) => (
    <tr className="tw-border-t tw-border-grey tw-bg-charcoal-10/60">
        <td className="tw-py-3 tw-pe-3 tw-ps-10 tw-align-top">
            {pr.detailUrl ? (
                <button type="button" onClick={() => onView(pr)} className="tw-font-medium tw-text-blue hover:tw-text-dark-blue">
                    {pr.reference}
                </button>
            ) : (
                <span className="tw-font-medium tw-text-charcoal">{pr.reference}</span>
            )}
        </td>
        <td className="tw-px-3 tw-py-3 tw-align-top">
            <TypeTag type={pr.type} />
        </td>
        <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{pr.procurement || '—'}</td>
        <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{pr.department || '—'}</td>
        <td className="tw-px-3 tw-py-3 tw-align-top tw-text-end tw-text-sm tw-text-charcoal">{tableMoney(currencySymbol, pr.value)}</td>
        <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{pr.requestor || '—'}</td>
        <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{pr.raised || '—'}</td>
        <td className="tw-px-3 tw-py-3 tw-align-top">
            <StatusPill status={pr.status} />
        </td>
        <td className="tw-px-3 tw-py-3 tw-align-top">{pr.detailUrl && <ViewButton row={pr} onView={onView} />}</td>
    </tr>
);

const ApprovalsRow = ({ row, currencySymbol, onView }) => {
    const [expanded, setExpanded] = useState(false);

    // EA and DC rows with an underlying PR are expandable (a DC is treated like an EA — it also
    // sits behind a PR, per the legacy EAs & DCs flow). PR rows carry no linked PR, so they stay leaves.
    const expandable = (row.type === RECORD_TYPES.EA || row.type === RECORD_TYPES.DC) && !!row.purchaseRequisition;

    return (
        <>
            <tr className="tw-border-t tw-border-grey hover:tw-bg-charcoal-10">
                <td className="tw-px-3 tw-py-3 tw-align-top">
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                        {expandable ? (
                            <button
                                type="button"
                                onClick={() => setExpanded((prev) => !prev)}
                                className="tw-inline-flex tw-shrink-0 tw-text-charcoal-50 hover:tw-text-charcoal"
                                aria-expanded={expanded}
                                aria-label={expanded ? $t('Hide linked PR') : $t('Show linked PR')}
                                title={expanded ? $t('Hide linked PR') : $t('Show linked PR')}
                            >
                                {expanded ? <ChevronDownIcon className="tw-w-3.5 tw-h-3.5" /> : <ChevronRightIcon className="tw-w-3.5 tw-h-3.5" />}
                            </button>
                        ) : (
                            <span className="tw-inline-block tw-w-3.5 tw-shrink-0" aria-hidden="true" />
                        )}
                        {row.detailUrl ? (
                            <button type="button" onClick={() => onView(row)} className="tw-font-medium tw-text-blue hover:tw-text-dark-blue">
                                {row.reference}
                            </button>
                        ) : (
                            <span className="tw-font-medium tw-text-charcoal">{row.reference}</span>
                        )}
                    </div>
                </td>
                <td className="tw-px-3 tw-py-3 tw-align-top">
                    <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-1">
                        <TypeTag type={row.type} />
                        {/* Beside the EA/DC tag rather than in a column of its own: it
                            qualifies how the commitment was raised, and only a minority
                            of rows carry it. */}
                        {row.raisedFromActual && (
                            <span className="tw-whitespace-nowrap tw-rounded tw-bg-charcoal-15 tw-px-1.5 tw-py-0.5 tw-text-2xs tw-text-charcoal-75">
                                {$t('Raised from actual')}
                            </span>
                        )}
                    </div>
                </td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal">{row.procurement}</td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{row.department || '—'}</td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-end tw-text-sm tw-text-charcoal">{tableMoney(currencySymbol, row.value)}</td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{row.requestor || '—'}</td>
                <td className="tw-px-3 tw-py-3 tw-align-top tw-text-sm tw-text-charcoal-75">{row.raised || '—'}</td>
                <td className="tw-px-3 tw-py-3 tw-align-top">
                    <StatusPill status={row.status} />
                </td>
                <td className="tw-px-3 tw-py-3 tw-align-top">{row.detailUrl && <ViewButton row={row} onView={onView} />}</td>
            </tr>
            {expandable && expanded && <LinkedPrRow pr={row.purchaseRequisition} currencySymbol={currencySymbol} onView={onView} />}
        </>
    );
};

/**
 * IB-701 — the unified Approvals register. Rows are PRs, EAs and DCs merged from the existing
 * feeds and pre-sorted pending-first by the caller. There is intentionally NO Approve action here
 * (approvals happen on the user's own approvals dashboard) — each row opens a read-only details
 * drawer via its Reference link and View (eye) shortcut. An EA row that has an underlying purchase
 * requisition can be expanded to reveal that PR inline. Reuses the Plan tab register's styling.
 */
const ApprovalsRegisterTable = ({ rows, currencySymbol, loading, emptyText, onView }) => (
    <div className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-p-5">
        {loading ? (
            <LoadingSpinner />
        ) : !rows || !rows.length ? (
            <p className="tw-py-6 tw-text-center tw-text-sm tw-text-charcoal-50">{emptyText}</p>
        ) : (
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-text-left tw-min-w-[64rem]">
                    <thead>
                        <tr className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">
                            {COLUMNS.map((column) => (
                                <th key={column.key} className={classNames('tw-px-3 tw-pb-2', { 'tw-text-end': column.align === 'end' })}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <ApprovalsRow key={row.key} row={row} currencySymbol={currencySymbol} onView={onView} />
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

export default ApprovalsRegisterTable;
